import Link from "next/link";
import { ArrowLeft, ChevronDown, User } from "react-feather";
import React, { useCallback, useEffect } from "react";
import { usePathname, useRouter, useParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { useLocale } from 'next-intl';
import useStore from "@store/index";

import styles from "@/styles/menu.module.scss";

import ServiceModal from "./modals/ServiceModal";
import LanguageSelector from "./modals/LanguageSelector";
import OpeningHoursModal from "./modals/OpeningHoursModal";
import { availableLanguages } from "_services/common.service";
import Image from "next/image";
import { CartLink } from "./navigation/cart-link";
import { Container } from "react-bootstrap";
import RouteLoader from "./route-loader";
import { submitCollection, submitSets } from "_services/submit.service";
import CustomTooltip from "./common/customtooltip";
import { event } from "@lib/ga";

export default function Navbar({ 
  isHomePage = false, 
  isBackButtonVisible = true, 
  backButtonLink = "/",
  pageTitle = "All Pizza", 
  isCartVisible = true,
  isShowFulfillmentDetails = true,
  className = null
}: {
  isHomePage?: boolean,
  isBackButtonVisible?: boolean,
  backButtonLink?: string,
  pageTitle?: string,
  isCartVisible?: boolean,
  isShowFulfillmentDetails?: boolean,
  className?: string | null
}) {
  const t = useTranslations();

  const router = useRouter();
  const locale = useLocale();
  const pathname = usePathname();
  const params = useParams();
  const activeCategory = decodeURIComponent((params?.menuname || "all").toString());
  

  const { companyDetails, hasHydrated, categories, deals, cart } = useStore((state) => state); 
  
  // console.log(categories, deals);
  // console.log(deals);


  const [showLanguageSelector, setShowLanguageSelector] = React.useState(false);
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const [showDealsDropDown, setShowDealsDropDown] = React.useState(false);
  
  const [isOnload, setIsOnload] = React.useState(true);
  const [isCompanyOpen, setIsCompanyOpen] = React.useState<boolean | null>(null);
  const [currentDate, setCurrentDate] = React.useState<Date | null>(null);

  const [totalCount, setTotalCount] =  React.useState(0);
  const [isLoadingCartCount, setIsLoadingCartCount] = React.useState(false);

  // // Effect to handle body scroll lock when language selector is open
  // useEffect(() => {
  //   if (showLanguageSelector || isCompanyOpen) {
  //     const originalOverflow = document.body.style.overflow;
      
  //     document.body.style.overflow = 'hidden';
      
  //     // Cleanup function to restore scroll when modal closes
  //     return () => {
  //       document.body.style.overflow = originalOverflow;
  //     };
  //   }
  // }, [showLanguageSelector,isCompanyOpen]);
  

  useEffect(() => {
    const shouldDisableScroll = showLanguageSelector || isCompanyOpen;

    if (shouldDisableScroll) {
      const originalStyles = {
        overflow: document.body.style.overflow,
        overscrollBehavior: document.body.style.overscrollBehavior,
      };

      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none'; 

      return () => {
        // Restore original styles
        document.body.style.overflow = originalStyles.overflow;
        document.body.style.overscrollBehavior = originalStyles.overscrollBehavior;
      };
    }
  }, [showLanguageSelector, isCompanyOpen]);

  const fetchData = useCallback(async () => {

    if(!hasHydrated || !isOnload) return;

    let isCompanyOpen = (companyDetails?.shopStatus === 'OPEN');
    let currentDate = (companyDetails?.serverTime) ? new Date(companyDetails.serverTime) : new Date();

    const serverTimeResponse = await submitSets(submitCollection.loadServerTime, {
      locationId: (companyDetails?.locationId || "")
    }, false, null);

    if (
      serverTimeResponse &&
      serverTimeResponse.status &&
      Object.keys(serverTimeResponse.extra).length > 0
    ) {
        const shopStatus = (serverTimeResponse.extra as { shopStatus?: string }).shopStatus || 'CLOSED';
        isCompanyOpen = (shopStatus === 'OPEN');

        const serverTimeStr = (serverTimeResponse.extra as { serverTime?: string }).serverTime || null;
        if(serverTimeStr) currentDate = new Date(serverTimeStr);
    }

    setIsCompanyOpen(isCompanyOpen);
    setCurrentDate(currentDate);
    setIsOnload(false);
  }, []);

  const didMountRef = React.useRef(false);
  useEffect(() => {
    if(!hasHydrated) return;

    if (didMountRef.current) return;
    didMountRef.current = true;

    fetchData();

  }, [hasHydrated]);

  const fetchCartCount = useCallback(async () => {
    if (cart && cart.lineItems && cart.lineItems.length > 0) {
      const totalItemsWithQuantity = cart?.lineItems?.reduce((total, item) => total + (parseInt(item.quantity.toString() || '0')), 0) || 0;
      setTotalCount(totalItemsWithQuantity);
      
      const cartID = cart?.orderId && cart?.orderId !== "" ? cart.orderId : '';
      // console.log(cartID);
      
      setIsLoadingCartCount(true);
      const itemDetailsRes = await submitSets(submitCollection.loadCartItemCount, {
          "orderId": cartID,
      }, false, null, false);
      
      if(itemDetailsRes && itemDetailsRes.status && itemDetailsRes.extra) {
        const itemCount = itemDetailsRes.extra.itemCount || 0;
        setTotalCount(itemCount);
      }

      setIsLoadingCartCount(false);
    }
  }, [cart]);

  const didMountCartRef = React.useRef(false);

  useEffect(() => {
    if(!hasHydrated) return;

    if (didMountCartRef.current) return;
    didMountCartRef.current = true;

    fetchCartCount();
  }, [hasHydrated]);

  useEffect(() => {
    if(!hasHydrated || isOnload || isLoadingCartCount) return;

    fetchCartCount();
  }, [cart]);

  if(!hasHydrated) return null;

  return (<>
    <RouteLoader />

    <div className={styles.mobileNavbar}>
      {isHomePage ? <>
      <header className={styles.header+ (className ? ' '+styles[className] : '')} data-classname={className || ''}>
        <Container>
          <div className={styles.logoContainer}>
            <Link href="#" id="nav-home-link" onClick={(e) => { 
              e.preventDefault(); 
              
              event({
                action: 'navigate_home',
                category: 'engagement',
                label: 'navbar_home_clicked'
              })
            
              router.push('/'); 
            }}>
              <div className={styles.logo}><Image src="/assets/images/main-logo.png" width={100} height={50} alt="Main logo" /></div>
            </Link>

            <OpeningHoursModal isCompanyOpen={isCompanyOpen} currentDate={currentDate} />
          </div>

          {/* <div className={styles.navLinks}>
            <Link href="#menu" id="nav-menu-link">Home</Link>
            <Link href="#about" id="nav-about-link">A</Link>
            <Link href="#contact" id="nav-contact-link">Contact</Link>
          </div> */}

          <div className={styles.navOptions}>
            <div className={styles.language} id="nav-language-selector-link" onClick={() => {
              setShowLanguageSelector(true);
              event({
                action: 'select_language',
                category: 'engagement',
                label: 'navbar_language_selector_clicked'
              })
            }}>
              {availableLanguages.find((lang: { code: string, name: string }) => lang.code === locale)?.code || 'EN'}
            </div>
            <div>
              <CartLink totalCount={totalCount} />
            </div>
          </div>
        </Container>
      </header>
    </>:<>
      <div className={styles.header}>
        <Container>
          <div className={styles.navOptions + ' ' + styles.fullNavOptions}>
            {isBackButtonVisible && <Link href={backButtonLink} className={styles.backButtonLink} onClick={(e) => { 
              e.preventDefault(); 

              event({
                action: 'navigate_back',
                category: 'engagement',
                label: 'navbar_back_button_clicked'
              });

              if(backButtonLink && backButtonLink !== "") {
                router.replace(backButtonLink);
              } else {
                router.back();
              } 
            }} id="nav-header-back-link"><div className={styles.backButton}>
              <ArrowLeft size={20} />
            </div></Link>}

            <CustomTooltip overlayContent={pageTitle} placement="auto" scrollElement={null} >
              <h1>{pageTitle.substring(0, 40)}{pageTitle.length > 40 ? '...' : ''}</h1>
            </CustomTooltip>

            <div>
              {isCartVisible && <CartLink totalCount={totalCount} />}
            </div>
          </div>
        </Container>
      </div>
    </>}
    </div>
    <div className={styles.desktopNavbar}>
      <header className={styles.header+ (className ? ' '+styles[className] : '')} data-classname={className || ''}>
        <Container>
          <div className={styles.logoContainer}>
            <Link href="#" id="nav-home-link" onClick={(e) => { 
              e.preventDefault(); 
            
              event({
                action: 'navigate_home',
                category: 'engagement',
                label: 'navbar_home_clicked'
              });

              router.push('/'); 
            }}>
              <div className={styles.logo}><Image src="/assets/images/main-logo.png" width={100} height={50} alt="Main logo" /></div>
            </Link>

            <OpeningHoursModal isCompanyOpen={isCompanyOpen} currentDate={currentDate} />
          </div>

          <div className={styles.navLinks}>
            <Link href="/" id="nav-menu-link" onClick={() => {
              event({
                action: 'navigate_home',
                category: 'engagement',
                label: 'navbar_home_clicked'
              })
            }} className={`${styles.navOptions} ${pathname === "/" ? styles.active : ""}`}>{t("Navbar.HOME")}</Link>
            {categories.map((category, index) => {
              const name =
                category.categoryName.charAt(0).toUpperCase() +
                category.categoryName.slice(1).toLowerCase();

                const isActive = pathname.includes("/menu") && activeCategory === category.categoryName;

              return (
                <Link
                  key={index}
                  href={`/menu/${category.categoryName}`}
                  className={`${styles.navOptions} ${isActive ? styles.active : ""}`}
                  onClick={() => {
                    event({
                      action: 'select_category',
                      category: 'engagement',
                      label: `navbar_category_${category.categoryName}_clicked`
                    })
                  }}
                  >
                  {name}
                </Link>

              );
            })}
            {deals?.map((deal, index) => (
              <div key={index} className={styles.dropdown} id="dropdown">
                <div className={styles.navOptions + ` ${ pathname.includes("/menu") && deal.subCategories.reduce((acc, subCategory) => acc || (activeCategory === subCategory.categoryName), false) ? styles.active : ""}`} id="dropdownTrigger" onClick={() => setShowDealsDropDown(!showDealsDropDown)}>
                    {t("common.more")}
                    <span style={{display: "inline-block",
                      transform: showDealsDropDown ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease-in-out", // smooth animation
                    }}>
                      <ChevronDown />
                    </span>
                </div>
                {showDealsDropDown &&(
                  <div className={styles.dropdown__menu} id="dropdownMenu">
                    {deal.subCategories && deal.subCategories.length > 0 && deal.subCategories.map((subCategory, subIndex: number) => (
                      <Link href={`/menu/${encodeURIComponent(subCategory.categoryName)}` } key={subIndex} id="nav-about-link" 
                      onClick={() => {
                        setShowDealsDropDown(false);
                        event({
                          action: 'select_deal_category',
                          category: 'engagement',
                          label: `navbar_deal_category_${subCategory.categoryName}_clicked`
                        })
                      }} className={styles.dropdownItems + ` ${pathname.includes("/menu") && (activeCategory === subCategory.categoryName) ? styles.active : ""}`}>
                        {subCategory.categoryName}
                      </Link>
                    ))}
                  </div>
                )}
            </div>
            ))}

            <Link href="/menu/all" id="nav-menu-link" onClick={() => {
              event({
                action: "navigate_menu",
                category: "engagement",
                label: "navbar_menu_clicked"
              })
            }} className={`${styles.navOptions} ${pathname.includes("/menu") && (decodeURIComponent((params?.menuname || "all").toString()) === "all") ? styles.active : ""}`}>{t("Navbar.MENU")}</Link>
            
          </div>

          <div className={styles.navOptions}>
            {/* <div className={styles.language} id="nav-language-selector-link" onClick={() => setShowLanguageSelector(true)}>
              {availableLanguages.find((lang: { code: string, name: string }) => lang.code === locale)?.code || 'EN'}
            </div> */}
            <CartLink totalCount={totalCount} />
            <div className={styles.profileContainer}>
              <div 
                className={styles.profile} 
                onClick={() => {
                  setShowProfileMenu(!showProfileMenu);
                  event({
                    action: 'toggle_profile_menu',
                    category: 'engagement',
                    label: showProfileMenu ? 'navbar_profile_menu_closed' : 'navbar_profile_menu_opened'
                  });
                }}
              >
                <User size={20} />
              </div>
              {showProfileMenu && (
                <div className={styles.profileMenu}>
                  {/* <div className={styles.menuItem} onClick={() => {
                    // router.push('/');
                    setShowProfileMenu(false);
                  }}>
                    Profile
                  </div> */}
                  <Link href={"/orders"} id="footer-orders-link" onClick={() => {
                    event({
                      action: 'navigate_orders',
                      category: 'engagement',
                      label: 'navbar_orders_clicked'
                    });
                  }} className={styles.dropdownItems}>
                    <div className={styles.menuItem} onClick={() => {
                      setShowProfileMenu(false);
                    }}>
                        {t("Navbar.ORDERS")}
                    </div>
                  </Link>
                  {/* <CartComponent /> */}
                  <Link href="/settings" id="footer-settings-link" onClick={() => {
                    event({
                      action: 'navigate_settings',
                      category: 'engagement',
                      label: 'navbar_settings_clicked'
                    });
                  }} className={styles.dropdownItems}>
                    <div className={styles.menuItem} onClick={() => {
                      setShowProfileMenu(false);
                    }}>
                        {t("Navbar.SETTINGS")}
                    </div>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </Container>
      </header>
    </div>
    

    {isShowFulfillmentDetails && <ServiceModal 
      isManualShow={false} 
      onToggleManualShow={() => {}} 
      isCompanyOpen={isCompanyOpen} 
      currentDate={currentDate}
      />}

    <LanguageSelector
      show={showLanguageSelector}
      onHide={() => setShowLanguageSelector(false)}
      selectedLanguage={locale} />
  </>);
}

